/**
 * Line/column helpers for the editor — same newline rules as legacy `split(/\r\n|\n|\r/)`,
 * without allocating an array over the whole buffer (critical for large files).
 */

export function countLogicalLines(text: string): number {
  if (text.length === 0) return 1;
  let lines = 1;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c === 10) lines++;
    else if (c === 13) {
      lines++;
      if (i + 1 < text.length && text.charCodeAt(i + 1) === 10) i++;
    }
  }
  return lines;
}

export function lineMetricsAtOffset(
  text: string,
  offset: number,
): { lineIndex0: number; line1: number; col1: number } {
  const capped = Math.max(0, Math.min(offset, text.length));
  let lineIndex0 = 0;
  let lineStart = 0;
  for (let i = 0; i < capped; i++) {
    const c = text.charCodeAt(i);
    if (c === 10) {
      lineIndex0++;
      lineStart = i + 1;
    } else if (c === 13) {
      lineIndex0++;
      // Treat CRLF as a single newline only when both chars are within scanned range.
      if (i + 1 < capped && text.charCodeAt(i + 1) === 10) {
        i++;
        lineStart = i + 1;
      } else {
        lineStart = i + 1;
      }
    }
  }
  return { lineIndex0, line1: lineIndex0 + 1, col1: capped - lineStart + 1 };
}

/** 0-based line index for the line containing `offset` (offset clamped to [0, length]). */
export function lineIndex0AtOffset(text: string, offset: number): number {
  return lineMetricsAtOffset(text, offset).lineIndex0;
}

/**
 * Character offset at the start of the given 0-based logical line index.
 * Returns `text.length` when `lineIndex0` exceeds the number of lines.
 * Handles `\n`, `\r\n`, and bare `\r` line endings (same rules as {@link countLogicalLines}).
 */
export function offsetAtLineIndex0(text: string, lineIndex0: number): number {
  if (lineIndex0 <= 0) return 0;
  let line = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c === 10 /* \n */) {
      line++;
      if (line === lineIndex0) return i + 1;
    } else if (c === 13 /* \r */) {
      line++;
      const next = i + 1 < text.length && text.charCodeAt(i + 1) === 10;
      if (line === lineIndex0) return next ? i + 2 : i + 1;
      if (next) i++;
    }
  }
  return text.length;
}

/** 1-based line/column (Notepad++-style status bar) at `offset`. */
export function lineCol1AtOffset(text: string, offset: number): { line: number; col: number } {
  const m = lineMetricsAtOffset(text, offset);
  return { line: m.line1, col: m.col1 };
}

/**
 * Half-open ranges `[start, endExclusive)` for each logical line — same newline rules as
 * `countLogicalLines` / `split(/\r\n|\n|\r/)` segments (used for wrap-aware line gutters).
 */
export function logicalLineRangesExclusive(text: string): Array<{ start: number; endExclusive: number }> {
  if (text.length === 0) {
    return [{ start: 0, endExclusive: 0 }];
  }
  const out: Array<{ start: number; endExclusive: number }> = [];
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c === 10) {
      out.push({ start, endExclusive: i + 1 });
      start = i + 1;
    } else if (c === 13) {
      if (i + 1 < text.length && text.charCodeAt(i + 1) === 10) {
        out.push({ start, endExclusive: i + 2 });
        i++;
        start = i + 1;
      } else {
        out.push({ start, endExclusive: i + 1 });
        start = i + 1;
      }
    }
  }
  out.push({ start, endExclusive: text.length });
  return out;
}
